import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Spinner, Nav, NavItem, NavLink, Button, Alert, Modal } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import { formatCreatedDate } from '../constants/utils';
import PermissionGuard from '../components/Common/PermissionGuard';
import classnames from 'classnames';

import { useTranslation } from 'react-i18next';

const Invitations = ({ refreshKey, onInvite }) => {
    const { t } = useTranslation();
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('');
    const [cancelError, setCancelError] = useState('');
    const [cancelModal, setCancelModal] = useState(false);
    const [selectedInvitation, setSelectedInvitation] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);

    const statusTabs = [
        { value: '', label: t('shared.all', 'All') },
        { value: 'PENDING', label: t('shared.pending', 'Pending') },
        { value: 'ACCEPTED', label: t('shared.accepted', 'Accepted') },
        { value: 'EXPIRED', label: t('shared.expired', 'Expired') },
        { value: 'CANCELLED', label: t('shared.cancelled', 'Cancelled') },
    ];

    const fetchInvitations = useCallback(async () => {
        setLoading(true);
        try {
            const params = activeTab ? `?status=${activeTab}` : '';
            const { data } = await api.get(`invitation/list${params}`);
            setInvitations(data.response || []);
        } catch (error) {
            console.error('Error fetching invitations:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    const handleCancel = async () => {
        if (!selectedInvitation) return;
        setCancelLoading(true);
        setCancelError('');
        try {
            await api.delete(`invitation/cancel?invitationId=${selectedInvitation.id}`);
            setCancelModal(false);
            setSelectedInvitation(null);
            fetchInvitations();
        } catch (error) {
            console.error('Error cancelling invitation:', error);
            setCancelError(error?.response?.data?.message || t('business.invitations.failed_cancel', 'Failed to cancel invitation'));
        } finally {
            setCancelLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING': return 'bg-warning text-dark';
            case 'ACCEPTED': return 'bg-success';
            case 'EXPIRED': return 'bg-secondary';
            case 'CANCELLED': return 'bg-danger';
            default: return 'bg-secondary';
        }
    };

    const formatStatus = (status) => {
        switch (status) {
            case 'PENDING': return t('shared.pending', 'Pending');
            case 'ACCEPTED': return t('shared.accepted', 'Accepted');
            case 'EXPIRED': return t('shared.expired', 'Expired');
            case 'CANCELLED': return t('shared.cancelled', 'Cancelled');
            default: return status;
        }
    };

    const formatRole = (role) => {
        if (!role) return '';
        return role.replace('ROLE_', '').replace(/_/g, ' ').toLowerCase()
            .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    // Refresh when parent triggers via refreshKey
    useEffect(() => {
        if (refreshKey > 0) {
            fetchInvitations();
        }
    }, [refreshKey, fetchInvitations]);

    return (
        <>
            <div className="mb-4 d-flex align-items-center gap-3">
                {onInvite && (
                    <PermissionGuard permission="invitation:create">
                        <button
                            type="button"
                            className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2"
                            onClick={onInvite}
                        >
                            <FeatherIcon icon="user-plus" size={18} />
                            {t('shared.invite_user', 'Invite User')}
                        </button>
                    </PermissionGuard>
                )}
            </div>

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t('shared.invitations', 'Invitations')}</h3>
                    </div>
                    <Nav tabs className="mt-3">
                        {statusTabs.map((tab) => (
                            <NavItem key={tab.value}>
                                <NavLink
                                    className={classnames({ active: activeTab === tab.value })}
                                    onClick={() => setActiveTab(tab.value)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {tab.label}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                </CardHeader>

                <CardBody>
                    <div className="table-responsive" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                        <table className="table table-hover mb-0" style={{ borderSpacing: '0.1rem', borderCollapse: 'separate' }}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('shared.recipient', 'Recipient')}</th>
                                    <th>{t('shared.role', 'Role')}</th>
                                    <th>{t('shared.status', 'Status')}</th>
                                    <th>{t('shared.invited_by', 'Invited By')}</th>
                                    <th>{t('shared.expires', 'Expires')}</th>
                                    <th>{t('shared.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {invitations.map((inv, ind) => (
                                        <motion.tr
                                            key={inv.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <th scope="row">{ind + 1}</th>
                                            <td>
                                                {inv.email || (inv.phoneCode && inv.contactNumber ? `+${inv.phoneCode}${inv.contactNumber}` : t('business.invitations.link_invite', 'Link invite'))}
                                            </td>
                                            <td>{formatRole(inv.targetRole)}</td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(inv.status)}`}>
                                                    {formatStatus(inv.status)}
                                                </span>
                                            </td>
                                            <td>{inv.invitedByName || '-'}</td>
                                            <td>{inv.expiresAt ? formatCreatedDate(inv.expiresAt) : '-'}</td>
                                            <td>
                                                {inv.status === 'PENDING' && (
                                                    <PermissionGuard permission="invitation:cancel">
                                                        <Button
                                                            color="outline-danger"
                                                            size="sm"
                                                            className="d-flex align-items-center justify-content-center"
                                                            onClick={() => {
                                                                setSelectedInvitation(inv);
                                                                setCancelError('');
                                                                setCancelModal(true);
                                                            }}
                                                            title={t('shared.cancel', 'Cancel')}
                                                        >
                                                            <FeatherIcon icon="x-circle" size={14} />
                                                        </Button>
                                                    </PermissionGuard>
                                                )}
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {loading && (
                        <div className="text-center py-3">
                            <Spinner color="primary" />
                        </div>
                    )}

                    {invitations.length === 0 && !loading && (
                        <div className="text-center py-5 text-muted">
                            {t('business.invitations.no_invitations', 'No invitations found')}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Cancel Confirmation Modal */}
            <Modal isOpen={cancelModal} toggle={() => { setCancelModal(false); setCancelError(''); }} centered>
                <div className="modal-header">
                    <h5 className="modal-title">{t('business.invitations.cancel_invitation', 'Cancel Invitation')}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => { setCancelModal(false); setCancelError(''); }}
                        aria-label="Close"
                    />
                </div>
                <div className="modal-body">
                    <p>
                        {t('business.invitations.confirm_cancel', 'Are you sure you want to cancel this invitation')}
                        {selectedInvitation?.email ? ` to ${selectedInvitation.email}` : ''}?
                    </p>
                    {cancelError && (
                        <Alert color="danger">{cancelError}</Alert>
                    )}
                </div>
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => { setCancelModal(false); setCancelError(''); }}
                    >
                        {t('shared.no', 'No')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleCancel}
                        disabled={cancelLoading}
                    >
                        {cancelLoading ? (
                            <>
                                <Spinner size="sm" className="me-1" /> {t('shared.cancelling', 'Cancelling...')}
                            </>
                        ) : (
                            t('shared.yes_cancel', 'Yes, Cancel')
                        )}
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default Invitations;
